import { money, pct } from './format.js';

// What the client owns underneath the fund names.
//
// This is the section a prospect cannot get anywhere else without paying for
// it. A statement lists funds; it never says that the same company sits inside
// nine of them, and that the "diversified" portfolio has a tenth of itself in
// one stock. Everything here is deliberately phrased as "at least", because
// YCharts returns only the largest holdings of each fund and the true figure
// can therefore only be higher.

// Below this, a company is a rounding error rather than a finding, and listing
// it would bury the ones that matter.
const MIN_SHOWN_PCT = 0.5;

export default function LookThroughSection({ lookThrough, names }) {
  if (!lookThrough || !lookThrough.companies?.length) return null;

  const shown = lookThrough.companies.filter(c => c.weightPct >= MIN_SHOWN_PCT).slice(0, 10);
  if (!shown.length) return null;

  const top = shown[0];
  const spread = lookThrough.companies.filter(c => c.viaCount > 1);
  const mostSpread = spread.slice().sort((a, b) => b.viaCount - a.viaCount)[0];

  return (
    <section className="asmt-card" id="holdings">
      <div className="asmt-card-head">
        <h2>What you actually own</h2>
        <span className="asmt-card-note">
          seen through {lookThrough.fundsAnalyzed.length} of your holdings
        </span>
      </div>
      <p className="asmt-lede">
        Your statements list funds. Funds hold companies, and the same company
        turns up inside many of them. Looking through every fund you hold, these
        are the businesses your money is actually invested in.
      </p>

      <div className="asmt-tiles" style={{ marginTop: 0 }}>
        <div className="asmt-tile">
          <div className="asmt-tile-label">Largest single company</div>
          <div className="asmt-tile-value">{pct(top.weightPct, 1)}</div>
          <div className="asmt-tile-note">{top.name}</div>
        </div>
        <div className="asmt-tile">
          <div className="asmt-tile-label">Top ten companies</div>
          <div className="asmt-tile-value">{pct(lookThrough.topTenWeightPct, 1)}</div>
          <div className="asmt-tile-note">of everything we could see through</div>
        </div>
        {mostSpread && (
          <div className="asmt-tile">
            <div className="asmt-tile-label">Held through the most funds</div>
            <div className="asmt-tile-value">{mostSpread.viaCount}</div>
            <div className="asmt-tile-note">funds all hold {mostSpread.name}</div>
          </div>
        )}
      </div>

      <table className="asmt-table">
        <thead>
          <tr>
            <th>Company</th>
            <th>Held through</th>
            <th className="num">At least</th>
            <th className="num">Share</th>
          </tr>
        </thead>
        <tbody>
          {shown.map(c => (
            <tr key={c.name}>
              <td>
                <strong>{c.name}</strong>
                {c.tickers.length > 0 && (
                  <span className="asmt-muted"> · {c.tickers.join(', ')}</span>
                )}
              </td>
              <td className="asmt-muted">
                {c.viaCount === 1 ? c.via[0] : `${c.viaCount} funds`}
              </td>
              <td className="num">{money(c.value)}</td>
              <td className="num">{pct(c.weightPct, 1)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="asmt-lookthrough-note">
        These are minimums. Fund managers publish their largest positions rather
        than every one, so we can see {pct(lookThrough.coveragePct, 0)} of your
        portfolio this way. Anything not visible would only add to the figures
        above, never reduce them.
        {lookThrough.fundsUnavailable.length > 0
          && ` ${lookThrough.fundsUnavailable.length} of your holdings publish nothing we can read, and are excluded entirely.`}
      </p>
    </section>
  );
}
